import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Drawer } from '@components/ui/Drawer/Drawer';
import { downloadDocument, downloadNameFor, type IDocument, type IDocumentVersion } from '@apis/documents';
import { StatusPill } from '../StatusPill/StatusPill';
import { VersionBadge } from '../VersionBadge/VersionBadge';
import { CompareModal } from '../CompareModal/CompareModal';
import { annotationsPath, canAnnotate } from '@components/Pages/AnnotationsPage/annotations.url';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { FilePreview } from '../FilePreview/FilePreview';
import { VersionEntry } from './VersionEntry/VersionEntry';
import { versionKey } from '../hooks/useVersionApproval';
import { documentVersions, headlineVersionOf } from '../utils/documentVersions';
import styles from './DocumentDrawer.module.css';
import { DownloadIcon, GitCompareIcon, LoaderIcon, PencilIcon, UploadCloudIcon } from '@/utils/icons';
import { formatProjectDateTime } from '@/utils/formatProjectDate';

interface IDocumentDrawerProps {
  doc: IDocument;
  siteId: string;
  onUploadNewVersion?: () => void;
  onDeleteVersion?: (version: IDocumentVersion) => void;
  onSubmitVersion?: (version: IDocumentVersion) => void;
  onDecideVersion?: (version: IDocumentVersion, decision: 'approve' | 'reject', comment: string) => Promise<boolean>;
  // the version is saved rather than published.
  compareVersion: number | null;
  onCompareVersionChange: (version: number | null) => void;
  busyVersionKey?: string | null; // documentId:seq` of the version whose approval request is in flight
  /** Kept mounted: `open` drops while the drawer slides out. */
  open: boolean;
  onClose: () => void;
}

export function DocumentDrawer({
  doc, siteId, onUploadNewVersion, onDeleteVersion,
  onSubmitVersion, onDecideVersion, compareVersion, onCompareVersionChange,
  busyVersionKey = null, open, onClose,
}: IDocumentDrawerProps) {
  /* Document stamps render in the PROJECT's zone, never the viewer's.
     Undefined only during the bootstrap window before the working site
     resolves — an em-dash then, rather than a date in a guessed zone. */
  const tz = useSelector(selectSiteTz);
  const { t } = useTranslation();
  const navigate = useNavigate();

  /** Opens the annotations page on one version, carrying the name the drawer
   *  shows — the page can only derive the physical one from the file id. */
  const openAnnotations = (fileId: string, version: number | null) =>
    navigate(annotationsPath(siteId, fileId, version), {
      state: {
        docName: doc.name,
        downloadName: downloadNameFor(doc.name, version ?? doc.version, doc.approvedVersion),
      },
    });
  
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);

  // The drawer stays mounted between openings, so each opening starts clean —
  // the last document's download state is not this one's.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setIsDownloading(false);
      setDownloadError(false);
    }
  }

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    setDownloadError(false);
    try {
      await downloadDocument(siteId, doc.id, downloadNameFor(doc.name, doc.version, doc.approvedVersion));
    } catch (err) {
      console.error('document download failed', err);
      setDownloadError(true);
    } finally {
      setIsDownloading(false);
    }
  };

  // A document with no recorded history still has one synthesised entry, so the
  // drawer always shows at least one dot.
  const versions: IDocumentVersion[] = documentVersions(doc);

  // The version the drawer presents — what the document-level Markup button
  // opens. Marking up doesn't need the version index: a file uploaded straight
  // into Genba is adopted by the save that follows it.
  const headlineVersion = headlineVersionOf(doc);
  const canAnnotateDoc = canAnnotate(doc.name, doc.id);

  // Dialog state comes from the URL; an invalid or stale param reads as closed.
  const compareOpen = compareVersion !== null && versions.length > 1;

  // The shared drawer owns the shell: backdrop, focus kept inside, Escape and
  // the slide in/out. The Compare dialog nests inside it.
  return (
    <Drawer
      open={open}
      onClose={onClose}
      ariaLabel={t('documents.detailsAria')}
      closeLabel={t('documents.close')}
    >

      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.name}>{doc.name}</h2>
        {doc.description.trim() !== '' && <p className={styles.description}>{doc.description}</p>}
      </div>

      <div className={styles.body} inert={!open}>
        {/* Metadata grid */}
        <div className={styles.metaGrid} data-tour="documents-drawer-meta">
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>{t('documents.meta.status')}</span>
            <StatusPill status={doc.status} />
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>{t('documents.meta.version')}</span>
            <VersionBadge version={doc.version} />
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>{t('documents.meta.lastUpdated')}</span>
            <span className={styles.metaValue}>{tz ? formatProjectDateTime(doc.updatedAt, tz) : '—'}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>{t('documents.meta.author')}</span>
            <span className={styles.metaValue}>{doc.updatedBy}</span>
          </div>
        </div>

        {/* Preview */}
        <div className={styles.section} data-tour="documents-drawer-preview">
          <span className={styles.sectionLabel}>{t('documents.preview')}</span>
          <FilePreview siteId={siteId} doc={doc} />
        </div>

        {/* Actions: two rows — download (always) + markup, then the version
            management pair. */}
        <div className={styles.actionsStack} data-tour="documents-drawer-actions">
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.downloadBtn}
              onClick={handleDownload}
              disabled={isDownloading}
              aria-busy={isDownloading}
            >
              {isDownloading ? (
                <>
                  <span className={styles.downloadSpinner}>
                    <LoaderIcon />
                  </span>
                  {t('documents.downloading')}
                </>
              ) : (
                <>
                  <DownloadIcon />
                  {t('documents.download')}
                </>
              )}
            </button>
            {canAnnotateDoc && (
              <button
                type="button"
                className={styles.compareBtn}
                onClick={() => openAnnotations(doc.id, headlineVersion?.version ?? null)}
              >
                <PencilIcon size={16} />
                {t('documents.annotations.action')}
              </button>
            )}
          </div>
          {(onUploadNewVersion || versions.length > 1) && (
            <div className={styles.actions}>
              {onUploadNewVersion && (
                <button
                  type="button"
                  className={styles.compareBtn}
                  onClick={onUploadNewVersion}
                >
                  <UploadCloudIcon size={16} />
                  {t('documents.uploadNewVersion')}
                </button>
              )}
              {versions.length > 1 && (
                <button
                  type="button"
                  className={styles.compareBtn}
                  onClick={() => onCompareVersionChange(versions[1]!.version)}
                >
                  <GitCompareIcon />
                  {t('documents.compare')}
                </button>
              )}
            </div>
          )}
        </div>
        {downloadError && <p className={styles.downloadError} role="alert">{t('documents.downloadError')}</p>}

        {/* Version history */}
        <div className={styles.section} data-tour="documents-drawer-versions">
          <span className={styles.sectionLabel}>{t('documents.versionHistory')}</span>
          <div className={styles.versionList}>
            {versions.map((v, i) => (
              <VersionEntry
                key={v.fileId}
                entry={v}
                isLatest={i === 0}
                siteId={siteId}
                downloadName={downloadNameFor(doc.name, v.version, doc.approvedVersion)}
                tz={tz}
                onDelete={v.canDelete && doc.documentId ? onDeleteVersion : undefined}
                onMarkup={canAnnotate(doc.name, v.fileId) ? (ver) => openAnnotations(ver.fileId, ver.version) : undefined}
                onSubmit={onSubmitVersion}
                onDecide={onDecideVersion}
                isBusy={!!doc.documentId && busyVersionKey === versionKey(doc.documentId, v.version)}
              />
            ))}
          </div>
        </div>
      </div>

      {compareOpen && (
        <CompareModal
          open={compareOpen}
          siteId={siteId}
          fileName={doc.name}
          approvedVersion={doc.approvedVersion}
          versions={versions}
          selectedVersion={compareVersion!}
          onSelectVersion={onCompareVersionChange}
          onClose={() => onCompareVersionChange(null)}
        />
      )}
    </Drawer>
  );
}